import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCopilotAction, useCopilotContext } from '@copilotkit/react-core'
import { F0HILActionConfirmation } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'
import { findOneCreateAction, useOneActionNames } from 'modules/core/lib/oneCreateActions'
import {
  OneCreationActionResolver,
  isNavigationResult,
} from 'modules/core/lib/oneCreateActions/types'

const OneCreateActionExecutor: FC<{
  useOneActionResolver: OneCreationActionResolver
  data: unknown
  summary: string
  respond: (message: string) => void
}> = ({ useOneActionResolver, data, summary, respond }) => {
  const { resolver } = useOneActionResolver()
  const { threadId } = useCopilotContext()
  const navigate = useNavigate()

  const confirmAction = async () => {
    const result = await resolver(data, threadId)

    if (result === undefined) {
      return respond('Something went wrong, we could not handle the request.')
    } else if (isNavigationResult(result)) {
      let responseMessage = `Tool executed successfully, redirecting user to ${
        result.to
      } with the following data: ${JSON.stringify(result.data)}`
      if (result.followUp) {
        responseMessage += `\n${result.followUp}`
      }
      navigate(result.to, { state: { oneCreationData: result.data, threadId } })
      respond(responseMessage)
    } else if (result.success) {
      return respond(`Entity created successfully: ${JSON.stringify(result.data)}`)
    } else {
      return respond(`Failed to create entity: ${JSON.stringify(result?.errors)}`)
    }
  }

  return (
    <F0HILActionConfirmation
      text={summary}
      confirmationText={i18n.t('common.confirm')}
      onConfirm={confirmAction}
      cancelText={i18n.t('common.cancel')}
      onCancel={() => respond('Action cancelled')}
    />
  )
}

export const AiAgentGlobalActions = () => {
  const availableActions = useOneActionNames()
  const createEntityAvailable = availableActions.length > 0 ? 'remote' : 'disabled'

  useCopilotAction({
    name: 'createEntity',
    description:
      'This action is only called when the user wants to create an entity and we have all the necessary data',
    followUp: true,
    available: createEntityAvailable,
    parameters: [
      {
        name: 'entityName',
        description: 'The name of the entity to create.',
        required: true,
        enum: availableActions,
      },
      {
        name: 'data',
        description: 'The data to create the entity with.',
        required: true,
      },
      {
        name: 'summary',
        description: 'A friendly summary with the data that will be used to create the entity.',
        required: true,
      },
    ],
    renderAndWaitForResponse: ({ status, args, respond }) => {
      if (status !== 'executing') {
        return <></>
      }

      const useOneActionResolver = findOneCreateAction(args.entityName)?.actionResolver

      if (!useOneActionResolver) {
        respond(
          `Something went wrong, the application is not ready to handle this request, the given entity name is not valid. The only valid names are: ${availableActions.join(
            ', '
          )}.`
        )
        return <></>
      }

      return (
        <OneCreateActionExecutor
          useOneActionResolver={useOneActionResolver}
          data={args.data}
          summary={args.summary}
          respond={respond}
        />
      )
    },
  })

  return null
}
